import { memo, useMemo } from 'react';
import { Button, Checkbox, Switch, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  IconBuilding,
  IconRoad,
  IconMapPin,
  IconHome,
  IconShoppingCart,
  IconTrees,
  IconLocation,
  IconLocationOff,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { FilterIconButton } from '../../../ui/FilterIconButton';
import { PlaceType, type PlaceTypeFilterProps } from '../types';
import styles from './PlaceTypeFilter.module.css';

const PLACE_TYPE_ICONS = {
  [PlaceType.ADDRESS]: IconMapPin,
  [PlaceType.BUILDING]: IconBuilding,
  [PlaceType.STREET]: IconRoad,
  [PlaceType.CITY]: IconHome,
  [PlaceType.POI]: IconShoppingCart,
  [PlaceType.NATURAL]: IconTrees,
};

/**
 * Place type filter button component - triggers filter display in dropdown
 */
export const PlaceTypeFilterButton = memo<
  PlaceTypeFilterProps & { onToggle: () => void; isOpen: boolean }
>(function PlaceTypeFilterButton({
  selectedTypes,
  onToggle,
  isOpen,
  className,
}) {
  const { t } = useTranslation();

  const totalTypesCount = Object.keys(PlaceType).length;
  const activeFilterCount = selectedTypes.size;
  const allTypesSelected = selectedTypes.size === totalTypesCount;
  const noTypesSelected = selectedTypes.size === 0;
  // Only show active state when some (but not all) types are selected
  const hasActiveFilters = activeFilterCount > 0 && !allTypesSelected;

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <FilterIconButton
        hasActiveFilters={hasActiveFilters}
        opened={isOpen}
        onClick={onToggle}
        size="compact"
        showChevron={true}
        aria-label={t('search.filter.label')}
      >
        {noTypesSelected && (
          <IconAlertTriangle className={styles.filterWarning} size={12} />
        )}
      </FilterIconButton>
    </div>
  );
});

/**
 * Place type filter content component - displayed in the main dropdown
 */
export const PlaceTypeFilterContent = memo<PlaceTypeFilterProps>(
  function PlaceTypeFilterContent({
    selectedTypes,
    onTypesChange,
    useLocationPriority,
    onLocationPriorityChange,
    hasUserLocation,
    className,
  }) {
    const { t } = useTranslation();

    const totalTypesCount = Object.keys(PlaceType).length;
    const allTypesSelected = selectedTypes.size === totalTypesCount;
    const noTypesSelected = selectedTypes.size === 0;

    const handleTypeToggle = (type: PlaceType) => {
      const newTypes = new Set(selectedTypes);
      if (newTypes.has(type)) {
        newTypes.delete(type);
      } else {
        newTypes.add(type);
      }
      onTypesChange(newTypes);
    };

    const handleSelectAll = () => {
      if (allTypesSelected) {
        // Clear all
        onTypesChange(new Set<PlaceType>());
      } else {
        // Select all
        onTypesChange(new Set(Object.values(PlaceType)));
      }
    };

    const getResetButtonText = useMemo(() => {
      if (selectedTypes.size === 0) {
        return t('search.filter.selectAll');
      }
      if (allTypesSelected) {
        return t('search.filter.clearAll');
      }
      return t('search.filter.selectAll');
    }, [selectedTypes.size, allTypesSelected, t]);

    return (
      <div className={`${styles.filterContent} ${className || ''}`}>
        <div className={styles.dropdownHeader}>
          <div className={styles.filterTitle}>{t('search.filter.header')}</div>
          <div className={styles.filterDescription}>
            {t('search.filter.description')}
          </div>
          <Button variant="light" size="xs" fullWidth onClick={handleSelectAll}>
            {getResetButtonText}
          </Button>
        </div>

        {noTypesSelected && (
          <div className={styles.warningMessage}>
            <IconAlertTriangle className={styles.warningIcon} />
            <span className={styles.warningText}>
              {t('search.filter.noTypesWarning')}
            </span>
          </div>
        )}

        <div className={styles.optionsList}>
          {Object.values(PlaceType).map((type) => {
            const TypeIcon = PLACE_TYPE_ICONS[type];
            const isSelected = selectedTypes.has(type);

            return (
              <div key={type} className={styles.optionItem}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleTypeToggle(type)}
                  classNames={{
                    root: styles.optionCheckboxRoot,
                    labelWrapper: styles.optionLabelWrapper,
                  }}
                  label={
                    <span className={styles.optionLabel}>
                      <TypeIcon className={styles.optionIcon} />
                      <span className={styles.optionText}>
                        {t(`search.filter.types.${type}`)}
                      </span>
                    </span>
                  }
                />
              </div>
            );
          })}
        </div>

        <div className={styles.locationToggle}>
          <div
            className={`${styles.locationOption} ${!hasUserLocation ? styles.locationDisabled : ''}`}
          >
            <div className={styles.locationInfo}>
              {hasUserLocation ? (
                <IconLocation className={styles.locationIcon} />
              ) : (
                <IconLocationOff className={styles.locationIcon} />
              )}
              <span className={styles.locationText}>
                {t('search.filter.prioritizeNearby')}
              </span>
            </div>
            <Tooltip
              label={t('search.filter.locationTooltip')}
              disabled={hasUserLocation}
              withArrow
              position="bottom"
            >
              <div>
                <Switch
                  checked={useLocationPriority && hasUserLocation}
                  onChange={(event) =>
                    onLocationPriorityChange(event.currentTarget.checked)
                  }
                  disabled={!hasUserLocation}
                  size="sm"
                  aria-label={t('search.filter.prioritizeNearby')}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }
);

// Keep the original export for backward compatibility during transition
export const PlaceTypeFilter = PlaceTypeFilterButton;
